import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Evaluation360Card } from './Evaluation360Card';
import { EvaluationPager, useEvaluationPager } from './EvaluationPager';
import { EVALUATION_PANEL_INNER_SURFACE, EvaluationPanel } from './EvaluationPanel';
import { EvaluationStartButton } from './EvaluationStartButton';

import type { User } from '@/types/directory/User';
import type { Evaluation360Entry } from '@/types/performance-profile';

import { isRefusedError } from '@/api/api';
import { useGetUsersByIds } from '@/api/directory';
import { useGetEvaluation360 } from '@/api/performance-profile';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';
import { isAnonymousFeedback } from '@/constants/review/FeedbackPrivacy';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useStartEvaluation } from '@/hooks/evaluations/useStartEvaluation';

interface PanelMessageProps {
    message: string;
    isError?: boolean;
}

const PanelMessage = ({ message, isError = false }: PanelMessageProps) => (
    <div className={EVALUATION_PANEL_INNER_SURFACE}>
        <div className={`${isError ? 'text-error' : 'text-obj-gray'} text-sm`}>{message}</div>
    </div>
);

interface Evaluation360PanelContentProps {
    isLoading: boolean;
    error: unknown;
    hasEvaluations: boolean;
    /** The evaluations of the page shown. */
    visible: Evaluation360Entry[];
    reviewerUsers: User[] | undefined;
}

const Evaluation360PanelContent = ({
    isLoading,
    error,
    hasEvaluations,
    visible,
    reviewerUsers,
}: Evaluation360PanelContentProps) => {
    const { t } = useTranslation();

    if (isLoading) {
        return <PanelMessage message={t('performanceProfile.evaluations.loading')} />;
    }

    // A refusal is no outage, and no absence either: it says what the module says of anything out of reach.
    if (isRefusedError(error)) {
        return (
            <EmptyState
                title={t('errors.notFoundOrInaccessibleTitle')}
                message={t('errors.notFoundOrInaccessibleMessage')}
            />
        );
    }

    if (error) {
        return (
            <PanelMessage
                isError
                message={t('performanceProfile.evaluations.evaluation360.loadError')}
            />
        );
    }

    if (!hasEvaluations) {
        return (
            <PanelMessage
                message={t('performanceProfile.evaluations.evaluation360.noEvaluations')}
            />
        );
    }

    return (
        <div className={'flex flex-col gap-(--gap-gap-2xl)'}>
            {visible.map((entry) => (
                <Evaluation360Card
                    key={entry.reviewInstanceId}
                    entry={entry}
                    reviewerUsers={reviewerUsers}
                />
            ))}
        </div>
    );
};

interface Evaluation360PanelProps {
    employeeId?: string;
    isEmployeeInactive?: boolean;
    canStartEvaluations?: boolean;
    className?: string;
}

export const Evaluation360Panel = ({
    employeeId,
    isEmployeeInactive,
    canStartEvaluations,
    className,
}: Evaluation360PanelProps) => {
    const { t } = useTranslation();
    const { start: startEvaluation, isCreating } = useStartEvaluation({
        evaluationType: EvaluationType.EVALUATION_360,
        firstStep: CycleInstanceSteps.ASSIGN_REVIEWER,
    });

    const { data: evaluations, isLoading, error } = useGetEvaluation360(employeeId);

    const reviewerUserIds = useMemo(() => {
        if (!evaluations) return [];
        const ids = evaluations
            .filter((entry) => !isAnonymousFeedback(entry.feedbackPrivacy))
            .flatMap((entry) => entry.reviewerUserIds);
        return Array.from(new Set(ids));
    }, [evaluations]);

    const { data: reviewerUsers } = useGetUsersByIds({
        userIds: reviewerUserIds,
        identifier: 'evaluation360-panel',
    });

    const { getManagerLabel } = useCompanyInformation();
    const { pageIndex, setPageIndex, pageCount, visible } = useEvaluationPager(evaluations, 3);

    return (
        <EvaluationPanel
            title={t('performanceProfile.evaluations.evaluation360.title')}
            description={t('performanceProfile.evaluations.evaluation360.description', {
                managerLabel: getManagerLabel(),
            })}
            className={className}
        >
            <Evaluation360PanelContent
                isLoading={isLoading}
                error={error}
                hasEvaluations={Boolean(evaluations?.length)}
                visible={visible}
                reviewerUsers={reviewerUsers}
            />

            <EvaluationPager
                pageIndex={pageIndex}
                pageCount={pageCount}
                onPickPage={setPageIndex}
            />

            {!isEmployeeInactive && (
                <EvaluationStartButton
                    canStartEvaluations={canStartEvaluations}
                    disabled={isCreating}
                    onClick={() => startEvaluation(employeeId)}
                    tooltip={t('performanceProfile.evaluations.disabledTooltip')}
                    iconName={'essential-send'}
                >
                    {t('performanceProfile.evaluations.evaluation360.startButton')}
                </EvaluationStartButton>
            )}
        </EvaluationPanel>
    );
};
